import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  FiArrowLeft, FiBook, FiUser, FiCalendar, FiTag, 
  FiBookOpen, FiCheck, FiX, FiClock, FiInfo, FiExternalLink, FiGlobe, FiDownload,
  FiFileText, FiSend, FiEdit3
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import './BookDetail.css';

const BookDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [book, setBook] = useState(null);
  const [borrowing, setBorrowing] = useState(false);
  const [showBorrowModal, setShowBorrowModal] = useState(false);
  const [borrowSuccess, setBorrowSuccess] = useState(false);
  const [error, setError] = useState('');
  
  // Citation request states
  const [showCitationModal, setShowCitationModal] = useState(false);
  const [citationRequest, setCitationRequest] = useState({
    keperluan: '',
    catatan: ''
  });
  const [requestingCitation, setRequestingCitation] = useState(false);
  const [citationSuccess, setCitationSuccess] = useState(false);

  useEffect(() => {
    const fetchBook = async () => {
      try {
        const response = await api.getBookDetail(id);
        if (response.success) {
          setBook(response.data);
        } else {
          setError('Buku tidak ditemukan');
        }
      } catch (err) {
        setError('Gagal memuat data buku');
      } finally {
        setLoading(false);
      }
    };

    fetchBook();
  }, [id]);

  const handleBorrow = async () => {
    setBorrowing(true);
    setError('');

    try {
      const borrowDays = user?.role === 'dosen' ? 14 : 7;
      const returnDate = new Date();
      returnDate.setDate(returnDate.getDate() + borrowDays);

      const response = await api.requestBorrow({
        user_id: user?.id,
        book_id: id,
        tanggal_kembali: returnDate.toISOString().split('T')[0]
      });

      if (response.success) {
        setBorrowSuccess(true);
        setShowBorrowModal(false);
      } else {
        setError(response.message || 'Gagal meminjam buku');
      }
    } catch (err) {
      setError('Terjadi kesalahan saat meminjam buku');
    } finally {
      setBorrowing(false);
    }
  };

  const handleRequestCitation = async () => {
    if (!citationRequest.keperluan) {
      setError('Silakan pilih keperluan sitasi');
      return;
    }

    setRequestingCitation(true);
    setError('');

    try {
      const response = await api.requestCitation({
        id_buku: id,
        id_mahasiswa: user?.id,
        keperluan: citationRequest.keperluan,
        catatan: citationRequest.catatan
      });

      if (response.success) {
        setCitationSuccess(true);
        setShowCitationModal(false);
        setCitationRequest({ keperluan: '', catatan: '' });
      } else {
        setError(response.message || 'Gagal mengirim request sitasi');
      }
    } catch (err) {
      setError('Terjadi kesalahan. Silakan coba lagi.');
    } finally {
      setRequestingCitation(false);
    }
  };

  if (loading) {
    return (
      <div className="book-detail__loading">
        <div className="book-detail__loading-spinner"></div>
        <p>Memuat data buku...</p>
      </div>
    );
  }

  if (error && !book) {
    return (
      <div className="book-detail__error">
        <FiX className="book-detail__error-icon" />
        <h3>{error}</h3>
        <button onClick={() => navigate('/katalog')}>Kembali ke Katalog</button>
      </div>
    );
  }

  const borrowDays = user?.role === 'dosen' ? 14 : 7;
  
  // Check if it's a dosen-uploaded book (not from Open Library/Google Books)
  const isDosenUpload = book?.sumber_buku === 'Upload Dosen' || book?.id_pemilik;
  
  // Only mark as open source if it's truly from Open Library/Google Books, not dosen uploads
  const isOpenSource = !isDosenUpload && (
    book?.tipe_buku === 'external' || 
    book?.sumber_buku === 'Open Library' || 
    book?.sumber_buku === 'Google Books' ||
    (book?.kode_buku && (book?.kode_buku.startsWith('OL') || book?.kode_buku.startsWith('GB')))
  );
  
  const isReadable = book?.is_readable === 1 || book?.is_readable === '1';
  const isAvailable = isOpenSource || isDosenUpload || parseInt(book?.stok_tersedia) > 0;

  return (
    <div className="book-detail">
      {/* Back Button */}
      <button className="book-detail__back" onClick={() => navigate(-1)}>
        <FiArrowLeft /> Kembali
      </button>

      {/* Success Message */}
      {borrowSuccess && (
        <div className="book-detail__success">
          <FiCheck />
          <div>
            <strong>Pengajuan Peminjaman Berhasil!</strong>
            <p>Peminjaman Anda sedang menunggu konfirmasi admin. Silakan cek status di halaman peminjaman.</p>
          </div>
          <Link to="/peminjaman" className="book-detail__success-link">
            Lihat Status Peminjaman
          </Link>
        </div>
      )}

      {/* Main Content */}
      <div className="book-detail__content">
        {/* Cover */}
        <div className="book-detail__cover">
          <img 
            src={book?.cover_image || '/images/cover-default.png'} 
            alt={book?.judul || 'Cover Buku'}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/images/cover-default.png';
            }}
          />
          <span className={`book-detail__status ${
            isOpenSource ? (isReadable ? 'free' : 'opensource') 
            : isDosenUpload ? 'dosen-upload' 
            : isAvailable ? 'available' : 'unavailable'
          }`}>
            {isOpenSource 
              ? (isReadable ? (<><FiGlobe /> Akses Gratis</>) : (<><FiExternalLink /> Open Library</>)) 
              : isDosenUpload 
                ? (<><FiDownload /> Digital Dosen</>) 
                : isAvailable ? 'Tersedia' : 'Tidak Tersedia'}
          </span>
        </div>

        {/* Info */}
        <div className="book-detail__info">
          <h1 className="book-detail__title">{book?.judul}</h1>
          
          <div className="book-detail__meta">
            {/* Show dosen photo and name if dosen book */}
            {isDosenUpload && book?.nama_dosen ? (
              <div className="book-detail__meta-item dosen-author">
                <div className="dosen-photo">
                  <img 
                    src={book?.foto_dosen 
                      ? (book.foto_dosen.startsWith('http') ? book.foto_dosen : `/api/uploads/foto/${book.foto_dosen}`)
                      : '/images/default-avatar.png'
                    }
                    alt={book?.nama_dosen}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/images/default-avatar.png';
                    }}
                  />
                </div>
                <div className="dosen-info">
                  <span className="dosen-label">Penulis / Dosen</span>
                  <span className="dosen-name">{book?.nama_dosen}</span>
                </div>
              </div>
            ) : (
              <div className="book-detail__meta-item">
                <FiUser />
                <span>{book?.penulis || 'Unknown'}</span>
              </div>
            )}
            <div className="book-detail__meta-item">
              <FiCalendar />
              <span>{book?.tahun_terbit || '-'}</span>
            </div>
            <div className="book-detail__meta-item">
              <FiTag />
              <span>{book?.nama_kategori || 'Umum'}</span>
            </div>
          </div>

          {/* Availability Info */}
          {isOpenSource ? (
            <div className={`book-detail__opensource-info ${isReadable ? 'free' : ''}`}>
              {isReadable ? <FiGlobe className="opensource-icon" /> : <FiExternalLink className="opensource-icon" />}
              <div>
                <strong>{isReadable ? 'Buku Akses Gratis' : 'Buku Open Library'}</strong>
                <p>
                  {isReadable 
                    ? 'Buku ini tersedia secara gratis dan dapat dibaca online kapan saja melalui OpenLibrary.'
                    : 'Buku ini terdaftar di OpenLibrary. Anda dapat melihat informasi lengkap buku, namun akses baca mungkin memerlukan peminjaman atau pembelian.'}
                </p>
              </div>
            </div>
          ) : isDosenUpload ? (
            <div className="book-detail__opensource-info dosen-upload">
              <FiBookOpen className="opensource-icon" />
              <div>
                <strong>Buku Digital Dosen</strong>
                <p>
                  Buku ini diupload oleh dosen dan tersedia dalam format digital (PDF). 
                  Anda dapat mengakses buku ini secara online.
                </p>
              </div>
            </div>
          ) : (
            <div className="book-detail__availability">
              <div className="book-detail__availability-item">
                <span className="label">Total Eksemplar</span>
                <span className="value">{book?.stok_total || 0}</span>
              </div>
              <div className="book-detail__availability-item">
                <span className="label">Tersedia</span>
                <span className="value success">{book?.stok_tersedia || 0}</span>
              </div>
              <div className="book-detail__availability-item">
                <span className="label">Dipinjam</span>
                <span className="value warning">{(parseInt(book?.stok_total) || 0) - (parseInt(book?.stok_tersedia) || 0)}</span>
              </div>
            </div>
          )}

          {/* Borrow Info */}
          <div className="book-detail__borrow-info">
            <FiInfo />
            <span>
              Durasi peminjaman untuk {user?.role === 'dosen' ? 'Dosen' : 'Mahasiswa'}: 
              <strong> {borrowDays} hari</strong>
            </span>
          </div>

          {/* Actions */}
          <div className="book-detail__actions">
            {isOpenSource ? (
              <a 
                href={book?.link_external} 
                target="_blank" 
                rel="noopener noreferrer"
                className={`book-detail__access-btn ${isReadable ? 'free' : 'info'}`}
              >
                <FiExternalLink />
                {isReadable ? 'Baca Buku Gratis' : 'Lihat di Open Library'}
              </a>
            ) : isDosenUpload && book?.link_external ? (
              <a 
                href={book.link_external.startsWith('http') ? book.link_external : `${book.link_external}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="book-detail__access-btn dosen"
              >
                <FiDownload />
                Baca / Download PDF
              </a>
            ) : (
              <button 
                className="book-detail__borrow-btn"
                onClick={() => setShowBorrowModal(true)}
                disabled={!isAvailable || borrowSuccess}
              >
                <FiBookOpen />
                {borrowSuccess ? 'Sudah Dipinjam' : 'Pinjam Buku'}
              </button>
            )}
            
            {/* Request Citation Button - Only for mahasiswa on dosen books */}
            {isDosenUpload && user?.role === 'mahasiswa' && (
              <button 
                className="book-detail__citation-btn"
                onClick={() => setShowCitationModal(true)}
                disabled={citationSuccess}
              >
                <FiFileText />
                {citationSuccess ? 'Request Terkirim' : 'Request Sitasi'}
              </button>
            )}
          </div>

          {/* Citation Success Message */}
          {citationSuccess && (
            <div className="book-detail__citation-success">
              <FiCheck />
              <span>Request sitasi berhasil dikirim! Dosen pemilik buku akan menghubungi Anda.</span>
            </div>
          )}

          {error && (
            <div className="book-detail__error-message">
              <FiX /> {error}
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      {book?.deskripsi && (
        <div className="book-detail__description">
          <h3>Deskripsi</h3>
          <p>{book.deskripsi}</p>
        </div>
      )}

      {/* Additional Info */}
      <div className="book-detail__additional">
        <h3>Informasi Tambahan</h3>
        <div className="book-detail__additional-grid">
          <div className="book-detail__additional-item">
            <span className="label">ISBN</span>
            <span className="value">{book?.isbn || '-'}</span>
          </div>
          <div className="book-detail__additional-item">
            <span className="label">Penerbit</span>
            <span className="value">{book?.nama_penerbit || '-'}</span>
          </div>
          <div className="book-detail__additional-item">
            <span className="label">Bahasa</span>
            <span className="value">{book?.bahasa || 'Indonesia'}</span>
          </div>
          <div className="book-detail__additional-item">
            <span className="label">Halaman</span>
            <span className="value">{book?.jumlah_halaman || '-'}</span>
          </div>
        </div>
      </div>

      {/* Borrow Modal */}
      {showBorrowModal && (
        <div className="book-detail__modal-overlay" onClick={() => setShowBorrowModal(false)}>
          <div className="book-detail__modal" onClick={(e) => e.stopPropagation()}>
            <div className="book-detail__modal-header">
              <h3>Konfirmasi Peminjaman</h3>
              <button onClick={() => setShowBorrowModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="book-detail__modal-body">
              <div className="book-detail__modal-book">
                <FiBook />
                <div>
                  <strong>{book?.judul}</strong>
                  <span>{book?.pengarang}</span>
                </div>
              </div>
              <div className="book-detail__modal-info">
                <div className="info-row">
                  <FiClock />
                  <span>Durasi Peminjaman: <strong>{borrowDays} hari</strong></span>
                </div>
                <div className="info-row">
                  <FiCalendar />
                  <span>
                    Tanggal Kembali: 
                    <strong>
                      {' ' + new Date(Date.now() + borrowDays * 24 * 60 * 60 * 1000)
                        .toLocaleDateString('id-ID', { 
                          day: 'numeric', 
                          month: 'long', 
                          year: 'numeric' 
                        })}
                    </strong>
                  </span>
                </div>
              </div>
              <p className="book-detail__modal-note">
                Dengan melakukan peminjaman, Anda setuju untuk mengembalikan buku 
                tepat waktu dan menjaga kondisi buku.
              </p>
            </div>
            <div className="book-detail__modal-actions">
              <button 
                className="cancel-btn" 
                onClick={() => setShowBorrowModal(false)}
              >
                Batal
              </button>
              <button 
                className="confirm-btn" 
                onClick={handleBorrow}
                disabled={borrowing}
              >
                {borrowing ? 'Memproses...' : 'Konfirmasi Pinjam'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Citation Request Modal */}
      {showCitationModal && (
        <div className="book-detail__modal-overlay" onClick={() => setShowCitationModal(false)}>
          <div className="book-detail__modal citation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="book-detail__modal-header">
              <h3><FiFileText /> Request Sitasi</h3>
              <button onClick={() => setShowCitationModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="book-detail__modal-body">
              <div className="book-detail__modal-book">
                <FiBook />
                <div>
                  <strong>{book?.judul}</strong>
                  <span>{book?.penulis}</span>
                </div>
              </div>
              
              <div className="citation-form">
                <div className="form-group">
                  <label><FiEdit3 /> Keperluan Sitasi <span className="required">*</span></label>
                  <select 
                    value={citationRequest.keperluan}
                    onChange={(e) => setCitationRequest({...citationRequest, keperluan: e.target.value})}
                    required
                  >
                    <option value="">-- Pilih Keperluan --</option>
                    <option value="Skripsi">Skripsi</option>
                    <option value="Tugas Akhir">Tugas Akhir</option>
                    <option value="Karya Ilmiah">Karya Ilmiah</option>
                    <option value="Jurnal">Jurnal</option>
                    <option value="Makalah">Makalah</option>
                    <option value="Lainnya">Lainnya</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label><FiEdit3 /> Catatan (Opsional)</label>
                  <textarea 
                    placeholder="Tuliskan catatan tambahan untuk dosen pemilik buku..."
                    value={citationRequest.catatan}
                    onChange={(e) => setCitationRequest({...citationRequest, catatan: e.target.value})}
                    rows={4}
                  />
                </div>
              </div>
              
              <p className="book-detail__modal-note">
                Request sitasi akan dikirim ke dosen pemilik buku. Dosen akan menghubungi 
                Anda melalui email atau kontak yang terdaftar.
              </p>
            </div>
            <div className="book-detail__modal-actions">
              <button 
                className="cancel-btn" 
                onClick={() => setShowCitationModal(false)}
              >
                Batal
              </button>
              <button 
                className="confirm-btn citation" 
                onClick={handleRequestCitation}
                disabled={requestingCitation || !citationRequest.keperluan}
              >
                <FiSend />
                {requestingCitation ? 'Mengirim...' : 'Kirim Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookDetail;
